import React, {
    FC, PropsWithChildren, ReactElement,
    useEffect, useRef, useState
}                                                           from "react";
import {filter as filterrx}                                 from "rxjs/operators";
import { docKeyDown$ }                                      from "@sirius/ui-lib/src/subjects/DocKeyDown";
import {DocClick$}                                          from "@sirius/ui-lib/src/subjects/DocClick";
import {resizeObservable}                                   from "@sirius/ui-lib/src/subjects/ResizeObservable";
import {useWindowSize}                                      from "@sirius/ui-lib/src/subjects/WindowSize";
import {windowScroll$}                                      from "@sirius/ui-lib/src/subjects/WindowScroll";
import {HBox, VBox}                                         from "@sirius/ui-lib/src/blocks/Layout";

import {block}                                              from "./block";
import {
    Column, UiColumn, TableProps,
    UiSchema, TableHasScroll,
    SortFilterPopupStateType,
    SortFilterClearPopupStateType
}                                                           from "./@types";
import {Footer}                                             from "./Footer";
import {THead}                                              from "./THead";
import {TBody}                                              from "./TBody";
import {TFoot}                                              from "./TFoot";
import {Header}                                             from "./Header";
import {SortFilterPopup}                                    from "./SortFilterPopup";
import {SortFilterClearPopup}                               from "./SortFilterClearPopup";


const filterChildrenBySlot =
    (Slot:  React.JSXElementConstructor<any>) =>
    ({type, props:{children}}: ReactElement): boolean =>
        (type as React.JSXElementConstructor<unknown>) === Slot && !!children
;

const getUiColumn = ({uischema, column:{name, type, cellType}}: {uischema: UiSchema, column: Column }) => (
    name in uischema ? uischema[name] :
        type in uischema ? uischema[type] :
            cellType in uischema ? uischema[cellType] :
                uischema['default']
);

const rowSelectColumn: Column = {name: 'select', title: ''};
const rowEditColumn:   Column = {name: 'edit',   title: ''};

const isStickyCol = ({sticky = false}: Column) => sticky;

const getColumnsByProps = ({cols, RowEditElement, onRowSelect, uischema, hasRowEditStickyLast = true}: TableProps): (UiColumn)[] => {
    const lastStickyName = hasRowEditStickyLast && RowEditElement
        ? cols.filter(isStickyCol).map(({name})=>name)?.at(-1)
        : false
    ;

    const firstColumns = [
        onRowSelect
            ? ({
                ...rowSelectColumn,
                onRowSelect,
                sticky: cols.some(isStickyCol) ? true : void(0),
              })
            : null
    ];

    const restColumns: Column[] = hasRowEditStickyLast && lastStickyName
        ? cols.reduce((acc: Column[], col) =>
                col.name === lastStickyName
                    ? [...acc, col, {...rowEditColumn, sticky: true} ]
                    : [...acc, col ]
            , [])
        : cols.concat( RowEditElement ? rowEditColumn : null )
    ;

    return (
        [...firstColumns, ...restColumns]
            .filter( c => c !== null )
            .map(
                column => ({
                    column,
                    ...getUiColumn({
                        uischema,
                        column
                    })
                })
            )
    );
};

const DefaultEmptyElement: React.FC = () =>
    <HBox className={block.el('empty-element')}>
        Здесь ещё ничего нет
    </HBox>
;

let setSortFilterClearPopupTimer: ReturnType<typeof setTimeout>;

const getPopupWidth = (hasSort: boolean, hasFilter: boolean) =>
    !hasFilter && !hasSort
        ? 0
        : hasSort && hasFilter
                ? 274
                : hasFilter
                    ? 189
                    : 109
;

const Table: FC<PropsWithChildren<TableProps>> = (props) => {

    const {
        className = '',
        onRowSelect,
        checkedRows,
        RowEditElement,
        EmptyElement = DefaultEmptyElement,
        rows,
        cols,
        columnSortAsc,
        columnSortDesc,
        columnFilterChange,
        clearColSortFilter,
        children,
        hardWidthDisabled = false,
        RowClassByRow,
        SeparatorElement,
        closeEditOnScroll = false
    } = props;

    const tableRef      = useRef(null);
    const blockRef      = useRef(null);
    const wrapperRef    = useRef<HTMLDivElement>(null);
    const window        = useWindowSize();

    const [width,           setWidth]                      = useState<number>(null);
    const [Cols,            setCols]                       = useState<UiColumn[]>(getColumnsByProps(props));
    const [hasScroll,       setHasScroll]                  = useState<TableHasScroll>({top:false, left:false, scrollLeft: 0, clientWidth: 0});
    const [sortFilterPopup, setSortFilterPopup]            = useState<SortFilterPopupStateType>(null);
    const [sortFilterClearPopup, _setSortFilterClearPopup] = useState<SortFilterClearPopupStateType>(null);
    const [needUpdate, setNeedUpdate]                      = useState(false);

    const onScroll = () => {
        const {scrollTop, clientWidth, scrollWidth, scrollLeft} = wrapperRef.current;
        const top  = scrollTop  > 0;
        const left = clientWidth !== scrollWidth;
        setHasScroll({top, left, scrollLeft, clientWidth});
    }

    useEffect(
        () => {
            setCols( getColumnsByProps(props) );
            _setSortFilterClearPopup(null);
        },[onRowSelect, RowEditElement, cols]
    );


    const hideSortFilterPopup = () => {
        setSortFilterPopup( null);
    }

    useEffect(
        ()=>{
            const subEsc      = docKeyDown$.pipe( filterrx( ({code}: KeyboardEvent)=> code === 'Escape' ) ).subscribe( hideSortFilterPopup );
            const subDocClick = DocClick$.subscribe( hideSortFilterPopup );
            const subResize   = resizeObservable(blockRef.current, 200).subscribe( onScroll );
            const subWS       = windowScroll$.subscribe( onScroll );
            return ()=>{
                subEsc        .unsubscribe();
                subDocClick   .unsubscribe();
                subResize     .unsubscribe();
                subWS         .unsubscribe();
            }
        },[]
    );

    useEffect(
        ()=>{
            setWidth(blockRef?.current?.offsetWidth);
            onScroll();
        },[window]
    );
    useEffect(
        ()=>{
            onRowSelect && onRowSelect(checkedRows);
        },[JSON.stringify(checkedRows)]
    );

    useEffect(() => {
        if (!needUpdate) {
            setNeedUpdate(true);
        }
    }, [JSON.stringify(cols)]);

    useEffect(() => {
        if(needUpdate) {
            onScroll();
            setNeedUpdate(false);
        }
    }, [needUpdate]);

    const chArr    = React.Children.toArray(children) as ReactElement[];
    const HeaderEl = (chArr).find( filterChildrenBySlot(Header) );
    const FooterEl = (chArr).find( filterChildrenBySlot(Footer) );

    const sortFilterPopupColumn = sortFilterPopup !== null
        ? Cols.find( ({column:{name}}) => name === sortFilterPopup.name )
        : null
    ;

    const popupWidth =
        sortFilterPopupColumn
            ? getPopupWidth(sortFilterPopupColumn?.column?.sort !== void(0), sortFilterPopupColumn?.column?.filter !== void(0))
            : 0
    ;

    const sortFilterPopupParams = {
        name:   sortFilterPopup?.name,
        sort:   sortFilterPopupColumn?.column?.sort,
        filter: sortFilterPopupColumn?.column?.filter,
        offset: sortFilterPopup?.offset - (
                    !sortFilterPopupColumn?.column?.sticky
                        ? (hasScroll.clientWidth - sortFilterPopup?.offset) < popupWidth
                            ? (hasScroll.scrollLeft + popupWidth - sortFilterPopup?.width)
                            : hasScroll.scrollLeft
                        : 0
                )
    };

    useEffect(
        () => {
            if (sortFilterPopup !== null) {
                _setSortFilterClearPopup(null)
            }
        },[sortFilterPopup]
    );

    const holdSortFilterClearPopup = () => {
        clearTimeout(setSortFilterClearPopupTimer)
        setTimeout(
            ()=>{ clearTimeout(setSortFilterClearPopupTimer) },
            10
        )
    }

    const hideSortFilterClearPopup = () => {
        setSortFilterClearPopupTimer = setTimeout(
            () => {
                _setSortFilterClearPopup(null)
            },
            300
        )
    }

    const setSortFilterClearPopup = (col: number, offset: number, show: boolean) => {
        const {column} = Cols[col];
        const {filter, sort, name, sticky} = column;
        const hasSF = !!filter || !!sort;
        return ({currentTarget}: React.MouseEvent) => {
            if (hasSF){
                const target = currentTarget as unknown as HTMLElement;
                if (show) {
                    _setSortFilterClearPopup({target, name, offset, sticky});
                    holdSortFilterClearPopup();
                } else {
                    hideSortFilterClearPopup();
                }
            }
        };
    }

    const sortFilterClearPopupOffset =
        sortFilterClearPopup
            ? sortFilterClearPopup.offset - (!sortFilterClearPopup.sticky ? hasScroll.scrollLeft : 0 )
            : 0
    ;

    return  <VBox
                className={
                    block +
                    block.bod('scroll-top',  hasScroll.top ) +
                    block.bod('scroll-left', hasScroll.left) +
                    className
                }
                divRef={blockRef}
            >
                {
                    HeaderEl &&
                    <THead
                        el    =  {HeaderEl}
                        width =  {width}
                    />
                }
                <VBox
                    className={block.el('table-wrapper')}
                    divRef={wrapperRef}
                >
                    {
                        (sortFilterClearPopup && sortFilterPopupColumn === null) &&
                        <SortFilterClearPopup
                            {...sortFilterClearPopup}
                            hold               = { holdSortFilterClearPopup }
                            hide               = { hideSortFilterClearPopup }
                            offset             = { sortFilterClearPopupOffset }
                            clearColSortFilter = { clearColSortFilter(sortFilterClearPopup.name) }
                            rowWidth           = { hasScroll.clientWidth }
                        />
                    }
                    {

                        sortFilterPopupColumn &&
                        <SortFilterPopup
                            {...sortFilterPopupParams}
                            sortAsc      = {()=>columnSortAsc( sortFilterPopup.name)     }
                            sortDesc     = {()=>columnSortDesc( sortFilterPopup.name)    }
                            filterChange = {(filter)=>columnFilterChange( sortFilterPopup.name, filter)}
                        />
                    }
                    <table ref={tableRef} className={block.el('table')} cellSpacing="0" cellPadding="0">
                        <TBody
                            cols                    = {Cols}
                            rows                    = {rows}
                            width                   = {width}
                            checkedRows             = {checkedRows}
                            setCheckedRows          = {onRowSelect}
                            RowEditElement          = {RowEditElement}
                            setSortFilterPopup      = {setSortFilterPopup}
                            sortFilterPopup         = {sortFilterPopup}
                            setSortFilterClearPopup = {setSortFilterClearPopup}
                            hardWidthDisabled       = {hardWidthDisabled}
                            RowClassByRow           = {RowClassByRow}
                            SeparatorElement        = {SeparatorElement}
                            wrapperRef              = {wrapperRef}
                            closeEditOnScroll       = {closeEditOnScroll}
                        />
                    </table>
                    { rows.length === 0 && <EmptyElement /> }
                    <TFoot
                        el    = {FooterEl}
                        width = {width}
                    />
                </VBox>
            </VBox>
    ;
};



export {Table}
